import { useState } from 'react';
import {
  Cloud, Mail, Users, ArrowRight, CheckCircle, ChevronDown, Shield,
  FileText, Video, HardDrive, Stethoscope, Scale, Hammer, Building,
  Phone
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { SEO } from '../../components/layout/SEO';
import { FAQPageSchema } from '../../components/seo/FAQPageSchema';
import { ServicePageSchema } from '../../components/seo/ServicePageSchema';
import { PageLayout } from '../../components/layout/PageLayout';
import { SpotlightCard } from '../../components/ui/SpotlightCard';
import { ServiceNavigation } from '../../components/ui/ServiceNavigation';
import { LocationLinks } from '../../components/ui/LocationLinks';
import { motion } from 'framer-motion';
import { TrackedPhoneLink } from '../../components/ui/TrackedPhoneLink';
import { InlineQuoteForm } from '../../components/InlineQuoteForm';

export function Microsoft365Page() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const services = [
    {
      icon: Mail,
      title: "Exchange Online Email",
      description: "Professional business email with 50GB mailboxes, custom domains, and advanced spam filtering.",
      features: ["50GB Storage", "Custom Domain", "ActiveSync", "Anti-Spam"]
    },
    {
      icon: Video,
      title: "Microsoft Teams",
      description: "Unified communication platform for video conferencing, chat, and real-time collaboration.",
      features: ["HD Video", "Team Chat", "File Sharing", "Screen Share"]
    },
    {
      icon: HardDrive,
      title: "OneDrive & SharePoint",
      description: "Secure cloud storage enabling your team to access files from anywhere on any device.",
      features: ["1TB Storage", "File Sync", "Version History", "Secure Sharing"]
    },
    {
      icon: FileText,
      title: "Office Applications",
      description: "Access Word, Excel, PowerPoint, and Outlook on desktop, web, and mobile devices.",
      features: ["Desktop Apps", "Web Access", "Mobile Ready", "Always Up-to-date"]
    },
    {
      icon: Shield,
      title: "Security & Compliance",
      description: "Enterprise-grade protection with MFA, data loss prevention, and compliance tools.",
      features: ["MFA", "Encryption", "Threat Defense", "Compliance"]
    },
    {
      icon: Users,
      title: "Local Support",
      description: "We handle license management, user administration, and troubleshooting for you.",
      features: ["Admin Support", "User Mgmt", "Billing", "Migration"]
    }
  ];

  const implementationSteps = [
    { step: "01", title: "Assessment", description: "We evaluate your current setup and internet bandwidth." },
    { step: "02", title: "Planning", description: "Detailed migration strategy to minimize downtime." },
    { step: "03", title: "Migration", description: "Secure transfer of email, files, and settings." },
    { step: "04", title: "Configuration", description: "Security hardening and compliance setup." },
    { step: "05", title: "Training", description: "Helping your team get the most out of the tools." },
    { step: "06", title: "Support", description: "Ongoing local management and troubleshooting." }
  ];

  const industries = [
    { icon: Stethoscope, title: "Healthcare", desc: "HIPAA-compliant setup" },
    { icon: Scale, title: "Legal Firms", desc: "Secure document mgmt" },
    { icon: Building, title: "Finance", desc: "SEC/FINRA compliance" },
    { icon: Hammer, title: "Construction", desc: "Mobile field access" },
    { icon: Users, title: "Real Estate", desc: "Collaborative tools" },
    { icon: Building, title: "Non-Profits", desc: "Discounted licensing" }
  ];

  const faqItems = [
    {
      question: "How long does a Microsoft 365 migration take?",
      answer: "Most small business migrations are completed in 2-4 weeks. We perform the heavy lifting during off-hours to ensure zero disruption to your workday."
    },
    {
      question: "Will we lose any email during the switch?",
      answer: "No. We perform a complete migration ensuring every email, contact, and calendar event is transferred safely before the cutover."
    },
    {
      question: "Is Microsoft 365 secure/HIPAA compliant?",
      answer: "Yes, when configured correctly. We enable specific security features to meet HIPAA, GDPR, and other regulatory requirements."
    },
    {
      question: "Can we keep our current email address?",
      answer: "Yes, you keep your existing custom domain (you@yourbusiness.com). The only thing that changes is the reliability and features of the backend system."
    }
  ];

  return (
    <PageLayout>
      <SEO
        title="Microsoft 365 Support Boise & Meridian | XXL IT"
        description="Expert Microsoft 365 implementation and support. Email, Teams, SharePoint, and security solutions for Treasure Valley businesses. Call (208) 328-4500."
        path="/services/microsoft-365"
        keywords={['Microsoft 365 Boise', 'Office 365 migration', 'Teams setup']}
      />

      <FAQPageSchema faqItems={faqItems} pageUrl="https://xxlservices.com/services/microsoft-365" />
      <ServicePageSchema
        serviceName="Microsoft 365 Services"
        serviceDescription="Complete Microsoft 365 setup, migration, and support for Treasure Valley businesses."
        serviceType="Microsoft 365 Cloud Solutions"
        serviceOutput="Deployed Microsoft 365, migrated email and data"
      />

      {/* Hero Section */}
      <section className="relative pb-20 overflow-hidden">
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 text-blue-300 px-4 py-2 rounded-full mb-8 backdrop-blur-sm">
              <Cloud className="w-4 h-4" />
              <span className="text-sm font-medium">Cloud Productivity</span>
            </div>

            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Microsoft 365 for <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">Idaho Business</span>
            </h1>

            <p className="text-xl text-gray-300 mb-10 max-w-3xl mx-auto leading-relaxed">
              Modernize your workforce with professional email, Teams, and cloud storage.
              Locally managed migration and support.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center bg-blue-600 hover:bg-blue-500 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-blue-900/30 hover:-translate-y-1"
              >
                Start Migration
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
              <TrackedPhoneLink location="microsoft-365-hero" className="inline-flex items-center justify-center bg-transparent border-2 border-white text-white px-8 py-4 rounded-full text-lg font-bold hover:bg-white/10 transition-all duration-300">
                (208) 328-4500
              </TrackedPhoneLink>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { label: "Uptime", value: "99.9%" },
                { label: "Migration", value: "Zero Loss" },
                { label: "Support", value: "Local" },
                { label: "Security", value: "Enterprise" }
              ].map((stat, index) => (
                <div key={index} className="bg-white/5 border border-white/10 backdrop-blur-sm rounded-2xl p-6">
                  <div className="text-2xl font-bold text-white mb-1">{stat.value}</div>
                  <div className="text-blue-200 text-sm font-medium">{stat.label}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-navy-800 border-t border-white/5 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-600/5 rounded-full blur-3xl"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              The Complete Suite
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Everything your team needs to collaborate securely.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <SpotlightCard className="h-full p-8" fromColor="from-blue-500/20" toColor="to-purple-500/20">
                    <div className="w-14 h-14 bg-blue-900/50 border border-white/10 rounded-2xl flex items-center justify-center mb-6">
                      <Icon className="w-7 h-7 text-blue-400" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-3">{service.title}</h3>
                    <p className="text-gray-400 text-sm mb-4 leading-relaxed">{service.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {service.features.map((f, i) => (
                        <span key={i} className="text-xs bg-blue-500/10 text-blue-300 px-2 py-1 rounded border border-blue-500/20">{f}</span>
                      ))}
                    </div>
                  </SpotlightCard>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Plans */}
      <section className="py-24 bg-navy-900 border-t border-white/5">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">Pricing Plans</h2>
            <p className="text-gray-400">Simple per-user pricing. No long-term contracts.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              {
                name: 'Business Basic',
                price: '$6',
                desc: 'Web/Mobile Apps & Email',
                features: ['Web Office Apps', '50GB Email', 'Teams', '1TB OneDrive']
              },
              {
                name: 'Business Standard',
                price: '$12.50',
                desc: 'Desktop Apps & Webinar Tools',
                features: ['Everything in Basic', 'Desktop Office Apps', 'Webinar Hosting', 'Appointment Booking'],
                popular: true
              },
              {
                name: 'Business Premium',
                price: '$22',
                desc: 'Advanced Security & Device Mgmt',
                features: ['Everything in Standard', 'Advanced Security', 'Device Management', 'Cyber Threat Protection']
              }
            ].map((plan, index) => (
              <div key={index} className={`relative bg-navy-800 border ${plan.popular ? 'border-blue-500 ring-2 ring-blue-500/20' : 'border-white/10'} rounded-2xl p-8`}>
                {plan.popular && (
                  <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full uppercase">
                    Recommended
                  </div>
                )}
                <h3 className="text-xl font-bold text-white mb-2">{plan.name}</h3>
                <div className="mb-4">
                  <span className="text-4xl font-bold text-white">{plan.price}</span>
                  <span className="text-gray-500">/mo</span>
                </div>
                <p className="text-gray-400 text-sm mb-6 pb-6 border-b border-white/10">{plan.desc}</p>
                <ul className="space-y-3">
                  {plan.features.map((f, i) => (
                    <li key={i} className="flex items-center text-gray-300 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* Implementation Process */}
      <section className="py-24 bg-navy-800 border-t border-white/5">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">Migration Process</h2>
            <p className="text-gray-400">Zero downtime. Zero data loss.</p>
          </div>
          <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-4">
            {implementationSteps.map((step, i) => (
              <div key={i} className="bg-navy-900 border border-white/10 p-4 rounded-xl relative group hover:border-blue-500/30 transition-colors">
                <div className="text-2xl font-bold text-blue-500 mb-2">{step.step}</div>
                <h3 className="text-sm font-bold text-white mb-1">{step.title}</h3>
                <p className="text-gray-500 text-xs">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-24 bg-navy-900 border-t border-white/5">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Industries</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {industries.map((ind, i) => {
              const Icon = ind.icon;
              return (
                <div key={i} className="bg-navy-800 border border-white/5 p-6 rounded-xl flex flex-col items-center text-center hover:bg-white/5 transition-colors">
                  <Icon className="w-8 h-8 text-purple-400 mb-4" />
                  <h3 className="text-sm font-bold text-white mb-1">{ind.title}</h3>
                  <p className="text-xs text-gray-500">{ind.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-navy-800 border-t border-white/5">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center">
              Questions?
            </h2>
            <div className="space-y-4">
              {faqItems.map((item, index) => (
                <div key={index} className="bg-navy-900 border border-white/5 rounded-xl overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full flex items-center justify-between p-6 text-left hover:bg-white/5 transition-colors"
                  >
                    <span className="font-bold text-white pr-8">{item.question}</span>
                    <ChevronDown className={`w-5 h-5 text-blue-400 transition-transform ${openFaq === index ? 'rotate-180' : ''}`} />
                  </button>
                  {openFaq === index && (
                    <div className="px-6 pb-6 pt-0 text-gray-400 leading-relaxed text-sm border-t border-white/5 pt-4">
                      {item.answer}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Inline Quote Form */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <InlineQuoteForm
            source="microsoft-365-page"
            defaultService="IT Support"
            headline="Get a Free Microsoft 365 Quote"
          />
        </div>
      </section>


      <div className="container mx-auto px-4 pb-12">
        <ServiceNavigation currentService="microsoft-365" />
        <LocationLinks />
      </div>
    </PageLayout>
  );
}
